/* eslint-disable no-nested-ternary */
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useAPIError from '../hooks/useAPIError';
import { ErrorArgsType } from '../types/Types';
import { IconX } from '../Icons';
import { useAuthStore } from '../store/AuthContext';
import { useAppStore } from '../store/AppContext';
import routerLoader from '../i18n/router';
import { HostProvider } from '../utils/HostProvider';

const ErrorMessage: FC = () => {
  const {
    error,
    removeError,
    errorBack,
    errorBackImgPng,
    warning,
    oro,
    addOro,
  } = useAPIError();

  const { t } = useTranslation();
  const dataUser = useAuthStore();
  const dataApp = useAppStore();
  const isApp = dataUser.user.isApp === 'true';
  const status = error?.status;
  const isCaprabo = HostProvider.isCaprabo();
  const errorMessage = error?.message;
  const errorCode = errorBack?.errorCode;
  const warningMessage = warning?.message;
  const errorImgCode = errorBackImgPng?.errorCode;
  const errorStatus = error?.status;

  const hasActiveError = useMemo(() => {
    return !!errorMessage || !!errorCode || !!warningMessage || !!errorImgCode;
  }, [errorMessage, errorCode, warningMessage, errorImgCode]);

  const mapErrorArgsToTrans = useCallback((src?: ErrorArgsType[]) => {
    if (!src || src.length === 0) {
      return '';
    }
    return src.map((err: ErrorArgsType) => {
      if (!err || !err.key) {
        return null;
      }
      const key = err.key.split('.').join('-');
      const arg = err.arguments ? err.arguments[0] : null;
      return (
        <Trans
          key={err.key}
          i18nKey={key}
          values={{ key: arg }}
          components={{ strong: <strong />, i: <i /> }}
        />
      );
    });
  }, []);

  const titleModal = useCallback(() => {
    const src =
      errorBack && errorBack.title && errorBack.title.length > 0
        ? errorBack.title
        : errorBackImgPng?.title;
    return mapErrorArgsToTrans(src);
  }, [errorBack, errorBackImgPng, mapErrorArgsToTrans]);

  const userMessageModal = useCallback(() => {
    const src =
      errorBack && errorBack.userMessage && errorBack.userMessage.length > 0
        ? errorBack.userMessage
        : errorBackImgPng?.userMessage;
    return mapErrorArgsToTrans(src);
  }, [errorBack, errorBackImgPng, mapErrorArgsToTrans]);

  const titleClasName =
    oro && !error?.style ? 'modal__card-title-oro' : 'modal__card-title';

  const titleMessage = errorMessage || warningMessage || titleModal();

  const handleSubmit = useCallback(() => {
    removeError();
    addOro(false);
    if (warning?.message === undefined) {
      if (status === 'KO') {
        if (isApp) {
          document.location.href = `${HostProvider.getHost()}${
            dataApp.lang
          }/backTowebExterna`;
        } else {
          document.location.href = isCaprabo
            ? `${
                process.env.REACT_APP_API_CAPRABO
              }/clienteLogado/getTK?fromUrl=${
                process.env.REACT_APP_CLUB_CAPRABO
              }${dataApp.lang}/clubcaprabo/&fromApp=${t('caprabo_com')}`
            : `${process.env.REACT_APP_API}/clienteLogado/getTK?fromUrl=${
                process.env.REACT_APP_SERVER_EROSKI_ES
              }${t('link.eroskies')}eroski-club/&fromApp=${t('eroski_es')}`;
        }
      }
    } else if (status === 'TARJETA-KO') {
      window.location.href = routerLoader.LoadRouterByPath(
        'tarjetas',
        dataApp.lang,
      );
    }
  }, [removeError, addOro, warning, status, isApp, dataApp.lang, isCaprabo, t]);

  const saveInfo = useCallback(
    (e: KeyboardEvent) => {
      const ev = e as KeyboardEvent & { keyCode?: number };
      if (ev.key === 'Enter' && ev.keyCode === 13) {
        const element = document.getElementById(
          'modal-error',
        ) as HTMLElement | null;
        if (element) {
          element.removeEventListener('keydown', saveInfo as any, false);
        }
        ev.preventDefault();
        ev.stopPropagation();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  useEffect(() => {
    const element = document.getElementById(
      'modal-error',
    ) as HTMLElement | null;
    if (element && hasActiveError) {
      element.setAttribute('tabindex', '0');
      element.focus();
      element.addEventListener('keydown', saveInfo as any, false);
    }
    return () => {
      if (element) {
        element.removeEventListener('keydown', saveInfo as any, false);
      }
    };
  }, [hasActiveError, saveInfo]);

  useEffect(() => {
    if (errorStatus === 'TARJETA-KO') {
      addOro(true);
    }
  }, [errorStatus, addOro]);

  return (
    <div
      className={`modal modal--small ${hasActiveError ? 'is-active' : ''}`}
      id="modal-error"
    >
      <div className="modal__background" />
      <div className="modal__card u-text-center">
        <header className="modal__card-head u-flex">
          <div className={titleClasName}>{titleMessage}</div>
          {!errorBackImgPng?.imgPng && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              href="#"
              className="modal__card-close"
              modal-cerrar="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }}
            >
              <div className="icon icon-medium icon--close">
                <IconX classCss="" />
              </div>
            </a>
          )}
        </header>

        {errorBackImgPng?.imgPng && (
          <div className="modal__card-img">
            <img src={errorBackImgPng.imgPng as string} alt="" />
          </div>
        )}

        <section className="modal__card-body">
          {errorMessage ? <p /> : <p>{userMessageModal()}</p>}
          <div className="modal__botonera">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              className={
                oro && !error?.style ? 'btn btn-oro' : 'btn btn-primary'
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }}
            >
              {t('entendido')}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ErrorMessage;
